import { useLoading } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useNavigation } from '@react-navigation/native';
import { useResetRecoilState } from 'recoil';

import { useOrderOriginRequired } from '../../../hooks/useOrderOriginRequired';
import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketAdvisorOrderIdState,
  customerCartState,
  customerCartStatusState,
  productsCartState,
  productsCartStatusState,
} from '../../../store/batchTicket/atom';
import {
  normalizeAndVerifyBasketCustomerOrderItemResumeData,
  normalizeCustomerBasketAdvisorData,
  normalizeProductsBasketAdvisorData,
} from '../../../store/batchTicket/normalize';
import { BasketAdvisorListDataItem } from '../../BatchTicket/utils/types';
import { customerBasketDetailsService, productsBasketDetailsService } from '../services';
import { errorMessageBatchTicketRequestItem } from '../utils';

export interface useBatchTicketRequestItemProps {
  item: BasketAdvisorListDataItem;
}
export const useBatchTicketRequestItem = ({ item }: useBatchTicketRequestItemProps) => {
  const { verifyProduct } = useOrderOriginRequired();
  const { showToast, showToastFromError } = useToastCreator('toast-batch-ticket-details-request');

  const {
    addCustomerListToCart,
    updateBasketAdvisorOrderId,
    onChangeCustomerCartStatus,
    onChangeProductsCartStatus,
    addProductsListToCart,
    addBasketCustomerOrderItemResume,
  } = useBatchTicketActions();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const defaultLoadingDescription =
    'Este processo pode levar alguns segundos, não saia desta pagina.';

  const resetBasketAdvisorOrderIdState = useResetRecoilState(basketAdvisorOrderIdState);
  const resetCustomerCartState = useResetRecoilState(customerCartState);
  const resetCustomerCartStatusState = useResetRecoilState(customerCartStatusState);
  const resetProductsCartState = useResetRecoilState(productsCartState);
  const resetProductsCartStatusState = useResetRecoilState(productsCartStatusState);

  const { hide, show } = useLoading();

  const isSaleOffer = item.side === 'V';
  const validFlowOrders = [2, 3];
  const validStatusId = [1002];
  const invalidStatusToDetails = [1000, 1001, 1002];
  const basketAdvisorFlowIsValid = validFlowOrders.includes(item.basketAdvisorFlow.flowOrder);
  const basketAdvisorStatusIdIsValid = validStatusId.includes(item.basketAdvisorStatus.statusId);
  const basketAdvisorCanGoToDetails = !invalidStatusToDetails.includes(
    item.basketAdvisorStatus.statusId
  );
  const detailsButtonIsDisabled =
    (!isSaleOffer &&
      !(basketAdvisorFlowIsValid || basketAdvisorStatusIdIsValid || basketAdvisorCanGoToDetails)) ||
    (isSaleOffer && item.basketAdvisorFlow.flowOrder !== 4);

  const detailsButtonColor = detailsButtonIsDisabled ? 'neutral.light' : 'brand.tertiary.principal';

  const getCustomerBasketData = async () => {
    try {
      const response = await customerBasketDetailsService({
        basketAdvisorId: item.basketAdvisorOrderId,
      });

      return response;
    } catch (error) {
      throw new Error(error as never);
    }
  };

  const getOffersBasketData = async () => {
    try {
      show({
        title: 'Aguarde enquanto buscando pelos produtos da sua cesta.',
        description: defaultLoadingDescription,
      });

      const response = await productsBasketDetailsService({
        basketAdvisorId: item.basketAdvisorOrderId,
      });

      const normalizedData = normalizeProductsBasketAdvisorData(response.data || []);

      addProductsListToCart(normalizedData);
      onChangeCustomerCartStatus('complete');
      onChangeProductsCartStatus('start');
    } catch (error) {
      throw new Error(error as never);
    }
  };

  const updateCustomerBasket = async () => {
    show({
      title: 'Aguarde enquanto buscando pelos clientes da sua cesta.',
      description: defaultLoadingDescription,
    });

    const response = await getCustomerBasketData();

    const normalizedData = normalizeCustomerBasketAdvisorData(response?.data || []);

    addCustomerListToCart(normalizedData);
  };

  const handleNavigateToBatchTicketResume = async () => {
    show({
      title: 'Aguarde enquanto estamos preparando as informações para finalizar sua cesta.',
      description: defaultLoadingDescription,
    });

    const response = await getCustomerBasketData();

    const batchTicketResumeParams = {
      data: normalizeAndVerifyBasketCustomerOrderItemResumeData(response.data || [], verifyProduct),
      summary: item,
    };

    onChangeCustomerCartStatus('complete');
    onChangeProductsCartStatus('complete');
    addBasketCustomerOrderItemResume(batchTicketResumeParams);
    navigate('BatchTicketResume');
  };

  const handlePressDetailsButton = async () => {
    if (isSaleOffer) {
      return navigate('RedeemBatchTicketDetails', {
        basketAdvisorId: item.basketAdvisorOrderId,
        basketAdvisorStatus: item.basketAdvisorStatus,
      });
    }

    if (basketAdvisorCanGoToDetails) {
      return navigate('BatchTicketDetail', {
        basketAdvisorOrderId: item.basketAdvisorOrderId,
      });
    }

    try {
      resetProductsCartStatusState();
      resetCustomerCartStatusState();

      if (basketAdvisorFlowIsValid) {
        await updateCustomerBasket();
      }

      if ([3].includes(item.basketAdvisorFlow.flowOrder)) {
        await getOffersBasketData();
      }

      updateBasketAdvisorOrderId(item.basketAdvisorOrderId);

      if (basketAdvisorFlowIsValid) {
        navigate('BatchTicket');
      }

      if (basketAdvisorStatusIdIsValid) {
        await handleNavigateToBatchTicketResume();
      }
    } catch (error) {
      resetBasketAdvisorOrderIdState();
      resetCustomerCartState();
      resetCustomerCartStatusState();
      resetProductsCartState();
      resetProductsCartStatusState();

      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  return { detailsButtonIsDisabled, detailsButtonColor, handlePressDetailsButton };
};
